import React from 'react';
import { Trans } from '@lingui/macro';

import Calendar from '@rover/icons/svg/calendar.svg';
import CreditCard from '@rover/icons/svg/credit-card.svg';
import HelpBubble from '@rover/icons/svg/text-help.svg';
import { Box, Display, Flex, Heading, Link, Paragraph } from '@rover/kibble/core';
import { Props as BoxProps } from '@rover/kibble/core/Box';
import { Props as DisplayProps } from '@rover/kibble/core/Display';
import { Props as FlexProps } from '@rover/kibble/core/Flex';
import type { Props as HeadingProps } from '@rover/kibble/core/Heading';
import { Props as ParagraphProps } from '@rover/kibble/core/Paragraph';
import Section from '@rover/kibble/patterns/Section';

import { PageSectionId, useSectionRef } from '../hooks/useSectionRef';

export const IconOptions = {
  CALENDAR: <Calendar />,
  CREDIT_CARD: <CreditCard />,
  HELP_BUBBLE: <HelpBubble />,
};

type WhyChooseBrandWrapperProps = Omit<BoxProps, 'variant'> & {
  htmlRef?: React.RefObject<HTMLElement>;
};

const WhyChooseBrandWrapper = ({
  htmlRef,
  children,
  ...props
}: WhyChooseBrandWrapperProps): JSX.Element => {
  return (
    <Section pt={['8x', '12x']} pb={['0x', '8x']} htmlRef={htmlRef} {...props}>
      {children}
    </Section>
  );
};

WhyChooseBrandWrapper.Display = (props: DisplayProps): JSX.Element => (
  <Display
    size="500"
    webProps={{ as: 'h3', responsive: false }}
    textAlign="center"
    mb="8x"
    {...props}
  />
);

WhyChooseBrandWrapper.List = (props: FlexProps): JSX.Element => {
  return (
    <Flex
      webAs="ul"
      width="100%"
      pl="0x"
      flexDirection={['column', 'row']}
      alignItems={['center', 'flex-start']}
      justifyContent={['flex-start', 'space-between']}
      {...props}
    >
      {props.children}
    </Flex>
  );
};

WhyChooseBrandWrapper.ListItem = (props: BoxProps & { icon: React.ReactNode }): JSX.Element => {
  return (
    <Box
      webAs="li"
      mx="3x"
      mb={['8x', '0x']}
      textAlign="center"
      width="100%"
      maxWidth="300px"
      sx={{ listStyleType: 'none' }}
      {...props}
    >
      <Box mb="3x">{props.icon}</Box>
      {props.children}
    </Box>
  );
};

WhyChooseBrandWrapper.ItemHeading = (props: HeadingProps): JSX.Element => (
  <Heading size="300" mb="3x">
    {props.children}
  </Heading>
);

WhyChooseBrandWrapper.ItemContent = (props: ParagraphProps): JSX.Element => {
  return <Paragraph {...props} />;
};

export type Props = {
  brandName: string;
  supportUrl: string;
};

const WhyChooseBrand: React.FC<Props> = ({ brandName, supportUrl }: Props) => {
  const whyChooseBrandRef = useSectionRef(PageSectionId.WhyChooseBrand);

  return (
    <WhyChooseBrandWrapper htmlRef={whyChooseBrandRef}>
      <WhyChooseBrandWrapper.Display>
        <Trans>Why Choose {brandName}?</Trans>
      </WhyChooseBrandWrapper.Display>
      <WhyChooseBrandWrapper.List>
        <WhyChooseBrandWrapper.ListItem icon={IconOptions.CREDIT_CARD}>
          <WhyChooseBrandWrapper.ItemHeading>
            <Trans>Cashless payments</Trans>
          </WhyChooseBrandWrapper.ItemHeading>
          <WhyChooseBrandWrapper.ItemContent>
            <Trans>
              Book and pay through {brandName}’s secured platform—no cash or checks required.
            </Trans>
          </WhyChooseBrandWrapper.ItemContent>
        </WhyChooseBrandWrapper.ListItem>
        <WhyChooseBrandWrapper.ListItem icon={IconOptions.CALENDAR}>
          <WhyChooseBrandWrapper.ItemHeading>
            <Trans>Reservation protection</Trans>
          </WhyChooseBrandWrapper.ItemHeading>
          <WhyChooseBrandWrapper.ItemContent>
            <Trans>
              It doesn’t happen often, but if your sitter has to cancel, we’ll work with you to find
              a new sitter.
            </Trans>
          </WhyChooseBrandWrapper.ItemContent>
        </WhyChooseBrandWrapper.ListItem>
        <WhyChooseBrandWrapper.ListItem icon={IconOptions.HELP_BUBBLE}>
          <WhyChooseBrandWrapper.ItemHeading>
            <Trans>World-class support</Trans>
          </WhyChooseBrandWrapper.ItemHeading>
          <WhyChooseBrandWrapper.ItemContent>
            <Trans>
              We’re friendly, knowledgable, and total pet fanatics. You can{' '}
              <Link href={supportUrl}>get in touch</Link> with us anytime.
            </Trans>
          </WhyChooseBrandWrapper.ItemContent>
        </WhyChooseBrandWrapper.ListItem>
      </WhyChooseBrandWrapper.List>
    </WhyChooseBrandWrapper>
  );
};

export default WhyChooseBrand;
